/**
 * Shared lowering context (W1-04 core-ir): the document view, the options, a lazily built Phira-exact tempo map,
 * the asset path mapping and the report accumulator. Every lowering entry point (`Lower.lowerChart`,
 * `lowerLine`, `lowerTrack`, `lowerExtra`) creates one; `lowerChart` uses a single context for the whole chart, so
 * its report is the sum of the parts and its output equals assembling the parts.
 */

import { buildTempoMap } from '../doc/container';
import { isGifAsset } from '../model/lineKind';
import type { AssetId, ChartDoc, DocIndexes, GroupId, Line, LineGroup, LineId } from '../model/types';
import type { TempoMap } from '../time/types';
import type { BakedSpeedReport, IrLine, LowerOptions, LowerReport } from './types';
import type { StaleCounter } from './triples';

/** Mutable form of `LowerReport` used while lowering. */
export interface LowerAcc extends StaleCounter {
  readonly bakedSpeed: BakedSpeedReport[];
  readonly paddedLines: LineId[];
  staleRawTriples: number;
  gapsMaterialised: number;
}

export function createLowerAcc(): LowerAcc {
  return { bakedSpeed: [], paddedLines: [], staleRawTriples: 0, gapsMaterialised: 0 };
}

export function reportOf(acc: LowerAcc): LowerReport {
  return {
    bakedSpeed: [...acc.bakedSpeed],
    paddedLines: [...acc.paddedLines],
    staleRawTriples: acc.staleRawTriples,
    gapsMaterialised: acc.gapsMaterialised,
  };
}

export interface LowerCtx {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  readonly opts: LowerOptions;
  readonly acc: LowerAcc;
  /** Phira-exact tempo map of the document (built on first use). */
  tempo(): TempoMap;
  /** Package path of an asset (LowerOptions.assetPath, else the stored path; the id itself when unknown). */
  assetPath(asset: AssetId): string;
  /** The chart's judgeLineGroup list and each group's index in it (`groupLayout`, built on first use). */
  groups(): GroupLayout;
  /** RPE `Group` index of a line's group (0 = "Default" for no group or an unknown group). */
  groupIndex(group: GroupId | null): number;
}

/** RPE's implicit group 0, which holds every line without a group. */
export const DEFAULT_GROUP_NAME = 'Default';
/**
 * Name written for an unused position below the highest group `order` (left behind when a group is deleted or
 * moved). The editor never creates a group with an empty name, and Lift drops an unused gap entry again, so a
 * project round-trips with its group orders unchanged.
 */
export const GROUP_GAP_NAME = '';
/**
 * Highest `order` written at its own position. Larger orders (never produced by the commands, which only append
 * after the highest order) are packed after the highest written position instead of padding thousands of gaps.
 */
export const MAX_GROUP_SLOT = 4096;

/** The exported group list: `judgeLineGroup` and the index of every group in it. */
export interface GroupLayout {
  readonly names: readonly string[];
  readonly index: ReadonlyMap<GroupId, number>;
}

/**
 * The judgeLineGroup list of a document (LineGroup.order is the position in the list):
 *  - every group sits at its `order`; position 0 is "Default" (RPE's group of lines without a group) unless a group
 *    has order 0, which then also holds the lines without a group (RPE has no "no group");
 *  - unused positions below the highest order are written as `GROUP_GAP_NAME`;
 *  - groups the invariants reject (an order shared with a group of lower id, a negative or fractional order) and
 *    orders above `MAX_GROUP_SLOT` are packed after the highest position, by (order, id).
 */
export function groupLayout(doc: Pick<ChartDoc, 'groups'>): GroupLayout {
  const sorted: LineGroup[] = [...doc.groups.values()].sort((a, b) => a.order - b.order || a.id - b.id);
  const slots: (string | undefined)[] = [];
  const index = new Map<GroupId, number>();
  const packed: LineGroup[] = [];
  for (const g of sorted) {
    const o = g.order;
    if (Number.isInteger(o) && o >= 0 && o <= MAX_GROUP_SLOT && slots[o] === undefined) {
      slots[o] = g.name;
      index.set(g.id, o);
    } else packed.push(g);
  }
  const names: string[] = [];
  for (let i = 0; i < Math.max(slots.length, 1); i++)
    names.push(slots[i] ?? (i === 0 ? DEFAULT_GROUP_NAME : GROUP_GAP_NAME));
  for (const g of packed) {
    index.set(g.id, names.length);
    names.push(g.name);
  }
  return { names, index };
}

export function createLowerCtx(doc: ChartDoc, idx: DocIndexes, opts: LowerOptions, acc = createLowerAcc()): LowerCtx {
  let tempo: TempoMap | null = null;
  let layout: GroupLayout | null = null;
  const custom = opts.assetPath;
  const groups = (): GroupLayout => {
    layout ??= groupLayout(doc);
    return layout;
  };
  return {
    doc,
    idx,
    opts,
    acc,
    tempo(): TempoMap {
      tempo ??= buildTempoMap(doc, idx);
      return tempo;
    },
    assetPath(asset: AssetId): string {
      if (custom !== undefined) return custom(asset);
      return doc.assets.get(asset)?.path ?? String(asset);
    },
    groups,
    groupIndex(group: GroupId | null): number {
      if (group === null) return 0;
      return groups().index.get(group) ?? 0;
    },
  };
}

/** The built-in line texture name (exact string Phira compares with). */
export const BUILTIN_TEXTURE = 'line.png';

/** What the line's texture decodes as in Phira (IrLine.textureKind). */
export function textureKindOf(doc: Pick<ChartDoc, 'assets'>, line: Line): IrLine['textureKind'] {
  if (line.texture === null) return 'builtin';
  return isGifAsset(doc.assets.get(line.texture)) ? 'gif' : 'image';
}
